import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  useTheme,
  Button,
  keyframes,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { baseapi } from "../Environmentalurl";
import ComingSoon from "../Utils/ComingSoon";
import api from "../Utils/Api";
import APSPDCL from "../BBPS/APSPDCL";


const zoomInOut = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

// ✅ Static Mobile Prepaid always shown first
const STATIC_SERVICES = [
  {
    Category: "Mobile Prepaid",
    Keyword: "mobileprepaid",
    Image72: "/Assets/Mobile_recharge.png",
  },
  {
    Category: "APSPDCL",
    Keyword: "apspdcl",
    Image72: "/Assets/Apspdcl.jpg", 
  },
];



const ServiceGrid = ({ services, baseRoute }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(3, 1fr)",
          sm: "repeat(4, 1fr)",
          md: "repeat(5, 1fr)",
          lg: "repeat(6, 1fr)",
        },
        gap: { xs: 0.8, sm: 1.5, md: 2 },
        p: { xs: 0.5, md: 1.5 },
      }}
    >
      {services.map((item, index) => (
        <Box
          key={`${item.Keyword || item.Category}-${index}`}
          onClick={() =>
            item.Keyword
              ? navigate(`${baseRoute}/${item.Keyword}`)
              : navigate(baseRoute)
          }
          sx={{
            textAlign: "center",
            cursor: "pointer",
            p: { xs: 0.8, md: 1.5 },
            borderRadius: 2,
            minWidth: 0,
            background: "white",
            border: "1px solid #f0f0f0",
            transition: "all 0.2s",
            "&:hover": {
              transform: "translateY(-3px)",
              boxShadow: "0 4px 16px rgba(22,163,74,0.15)",
              borderColor: "#86efac",
              bgcolor: "#f0fdf4",
            },
          }}
        >
          {/* 🔹 Icon */}
          <Box
            component="img"
            src={item.Image72}
            alt={item.Category}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/Assets/default_icon.png";
            }}
            sx={{
              width: { xs: 32, sm: 40, md: 48 },
              height: { xs: 32, sm: 40, md: 48 },
              objectFit: "contain",
              mb: 0.5,
            }}
          />

          {/* 🔹 Label */}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: { xs: "8.5px", sm: "11px", md: "13px" },
              color: "#374151",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item.Category}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const BBPSServices = forwardRef(({ billPaymentRef }, ref) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [highlight, setHighlight] = useState(false);
  const [openComingSoon, setOpenComingSoon] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const bbpsServices = services.filter(
    (item) => item.Keyword?.toLowerCase() !== "apspdcl"
  );

  useImperativeHandle(ref, () => ({
    triggerHighlight() {
      setHighlight(true);
      setTimeout(() => setHighlight(false), 1500);
    },
  }));

  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);
      try {
        // ✅ Try calling api() — handle BOTH cases:
        // Case 1: api() returns raw parsed JSON directly
        // Case 2: api() returns a fetch Response object
        const result = await api(`${baseapi}/getbillavenuelist`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });

        let data = [];

        if (result && typeof result.json === "function") {
          // Case 2: it's a Response object
          if (result.ok) {
            data = await result.json();
          }
        } else if (Array.isArray(result)) {
          // Case 1: api() already returned parsed array
          data = result;
        } else if (result && typeof result === "object") {
          // Case 1b: api() returned an object — check common wrapper keys
          if (Array.isArray(result.data)) data = result.data;
          else if (Array.isArray(result.Data)) data = result.Data;
          else if (Array.isArray(result.result)) data = result.result;
          else if (Array.isArray(result.Result)) data = result.Result;
          else if (Array.isArray(result.services)) data = result.services;
          else {
            // last resort — log and use empty
            console.warn("Unexpected API response shape:", result);
          }
        }

        console.log("✅ Fetched services:", data); // check browser console

        setServices([...STATIC_SERVICES, ...data]);
      } catch (err) {
        console.error("❌ Error fetching services:", err);
        // Show at least static services on error
        setServices([...STATIC_SERVICES]);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  return (
    <>
      <Box
        ref={billPaymentRef}
        sx={{
          p: 2,
          mt: 4,
          borderRadius: 2,
          transition: "0.4s",
          backgroundColor: highlight ? "rgba(77,182,77,0.12)" : "transparent",
          boxShadow: highlight ? "0px 0px 18px rgba(77,182,77,0.5)" : "none",
        }}
      >
        <Typography
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: "bold",
            fontSize: { xs: "16px", md: "22px" },
            mb: 2,
          }}
        >
          <img
            src="/Assets/bbpslogo.png"
            alt="BBPS"
            style={{ width: "auto", height: "auto", maxWidth: 80 }}
          />
          BBPS Services
        </Typography>

        <Card
          sx={{
            backgroundColor: isDark ? "#0b1b30" : "#f5f5f5",
            borderRadius: 3,

            border: isDark
              ? "1px solid rgba(90, 126, 173, 0.49)"
              : "1px solid rgba(0,0,0,0.2)",
          }}
        >
          <CardContent
            sx={{
              p: { xs: 1, md: 2 },
              "&:last-child": { pb: { xs: 1, md: 2 } },
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "80px",
                }}
              >
                <CircularProgress color="inherit" size={25} />
              </Box>
            ) : (
              <ServiceGrid
                services={bbpsServices}
                baseRoute="/dashboard/bbps"
              />
            )}
          </CardContent>
        </Card>
      </Box>


      <Typography
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontWeight: "bold",
          fontSize: { xs: "16px", md: "22px" },
          mb: 2,
        }}
      >
        <img
          src="/Assets/APSPDCLCardlogo.png"
          alt="APSPDCL"
          style={{ width: "auto", height: "auto", maxWidth: 150 }}
        />
        APSPDCL Bill Payment
      </Typography>

      <Card
        sx={{
          backgroundColor: isDark ? "#0b1b30" : "#f5f5f5",
          borderRadius: 3,
          border: isDark
            ? "1px solid rgba(90,126,173,0.49)"
            : "1px solid rgba(0,0,0,0.2)",
        }}
      >
        <CardContent
          sx={{
            p: { xs: 1, md: 2 },
            "&:last-child": {
              pb: { xs: 1, md: 2 },
            },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(3, 1fr)",
                sm: "repeat(4, 1fr)",
                md: "repeat(5, 1fr)",
                lg: "repeat(6, 1fr)",
              },
              gap: { xs: 0.8, sm: 1.5, md: 2 },
              p: { xs: 0.5, md: 1.5 },
            }}
          >
            <Box
              onClick={() => navigate("/dashboard/apspdcl")}
              sx={{
                textAlign: "center",
                cursor: "pointer",
                p: { xs: 0.8, md: 1.5 },
                borderRadius: 2,
                minWidth: 0,
                background: "white",
                border: "1px solid #f0f0f0",
                transition: "all 0.2s",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 4px 16px rgba(22,163,74,0.15)",
                  borderColor: "#86efac",
                  bgcolor: "#f0fdf4",
                },
              }}
            >
              <Box
                component="img"
                src="/Assets/Apspdcl.jpg"
                alt="APSPDCL"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/Assets/default_icon.png";
                }}
                sx={{
                  width: { xs: 32, sm: 40, md: 48 },
                  height: { xs: 32, sm: 40, md: 48 },
                  objectFit: "contain",
                  mb: 0.5,
                }}
              />

              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "8.5px", sm: "11px", md: "13px" },
                  color: "#374151",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                APSPDCL
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Offers Section */}
      <Box mt={4} px={3}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            mb: 3,
            // background: "linear-gradient(40deg, #00b34a, #c8f84b)",
            color: "black",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            display: "inline-block",
          }}
        >
          Our Services Offers
        </Typography>

        <Grid container spacing={5} mt={3}>
          <Grid size={{ xs: 12, md: 6 }} item xs={12} md={6}>
            <Box
              sx={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: "linear-gradient(135deg,#ede9fe 0%,#ddd6fe 100%)",
                borderRadius: 3, p: 2.5, border: "1px solid #c4b5fd",
                flexDirection: { xs: "column", sm: "row" }, gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", color: "black" }}
                >
                  Apply for RuPay Card
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: "#444" }}>
                  Get exclusive benefits and rewards
                </Typography>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#3f51b5",
                    color: "#fff"
                  }}
                >
                  Apply Now
                </Button>
              </Box>
              <img
                src="/Assets/Rupay_card_img.png"
                alt="RuPay"
                style={{ width: 120, marginTop: 10 }}
              />
            </Box>
          </Grid>

          {/* --- Travel Offers --- */}
          <Grid size={{ xs: 12, md: 6 }} item xs={12} md={6}>
            <Box
              sx={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: "linear-gradient(135deg,#fef3c7 0%,#fde68a 100%)",
                borderRadius: 3, p: 2.5, border: "1px solid #fcd34d",
                flexDirection: { xs: "column", sm: "row" }, gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", color: "black" }}
                >
                  Travel Offers
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: "#444" }}>
                  Up to 20% cashback on bookings
                </Typography>
                <Button
                  variant="contained"
                  sx={{
                    background: "linear-gradient(90deg, #ff6f00, #d2691e)",
                    color: "#fff"
                  }}
                >
                  Book Now
                </Button>
              </Box>
              <img
                src="/Assets/plane_img.png"
                alt="Plane"
                style={{ width: 150, marginTop: 10 }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <ComingSoon
        open={openComingSoon}
        onClose={() => setOpenComingSoon(false)}
      />
    </>
  );
});

export default BBPSServices;
